
import { useCallback, useEffect, useRef, useState } from "react";

interface UseRealtimeTrackerVisibilityProps {
    autoScorll?: boolean;
    indicatorRef: React.RefObject<HTMLElement>;
    scrollContainerRef: React.RefObject<HTMLElement>;
    onVisibilityChange?: (isVisible: boolean) => void;
    onScrollIntoView?: (scrollLeft: number) => void;
    scrollBehavior?: ScrollBehavior;
    smoothScrollDuration?: number;
    intersectionThreshold?: number | number[];
    scrollDebounceMs?: number;
    autoScrollOnMount?: boolean;
}

interface UseRealtimeTrackerVisibilityReturn {
    isVisible: boolean;
    isUserScrolling: boolean;
    scrollToIndicator: () => void;
    getVisibilityState: () => { isVisible: boolean; isUserScrolling: boolean };
}

/**
 * Finalized hook:
 * - Immediate manual override on user scroll start
 * - On scroll end (debounced) check indicator visibility to decide override
 * - Programmatic scrolls do not set manual override
 * - IntersectionObserver clears manual override when indicator becomes visible
 */
export const useRealtimeTrackerVisibility = ({
    autoScorll,
    indicatorRef,
    scrollContainerRef,
    onVisibilityChange,
    onScrollIntoView,
    scrollBehavior = "smooth",
    smoothScrollDuration = 400,
    intersectionThreshold = 0,
    scrollDebounceMs = 150,
    autoScrollOnMount = true,
}: UseRealtimeTrackerVisibilityProps): UseRealtimeTrackerVisibilityReturn => {
    const [isVisible, setIsVisible] = useState(false);
    const [isUserScrolling, setIsUserScrolling] = useState(false);

    // timers / raf refs
    const rafRef = useRef<number | null>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const programmaticTimerRef = useRef<NodeJS.Timeout | null>(null);
    const mountTimerRef = useRef<NodeJS.Timeout | null>(null);
    const intersectionRef = useRef<IntersectionObserver | null>(null);

    // flags
    const programmaticScrollRef = useRef(false); // true when we programmatically scroll
    const manualOverrideRef = useRef(false); // true when user intentionally moved away
    const didAutoScrollOnMountRef = useRef(false);

    // last scrollLeft to detect movement
    const lastScrollLeftRef = useRef(0);

    // compute fraction of indicator visible inside container
    const computeVisibleFraction = useCallback(() => {
        const container = scrollContainerRef.current;
        const indicator = indicatorRef.current;
        if (!container || !indicator) return 0;
        const c = container.getBoundingClientRect();
        const i = indicator.getBoundingClientRect();
        const left = Math.max(c.left, i.left);
        const right = Math.min(c.right, i.right);
        const visible = Math.max(0, right - left);
        const width = i.width || 1;
        return visible / width;
    }, [indicatorRef, scrollContainerRef, autoScorll]);

    const isIndicatorVisibleByThreshold = useCallback(() => {
        const fraction = computeVisibleFraction();
        const threshold = Array.isArray(intersectionThreshold)
            ? Math.min(...intersectionThreshold)
            : (intersectionThreshold ?? 0);
        return fraction >= threshold;
    }, [computeVisibleFraction, intersectionThreshold, autoScorll]);

    // compute target scrollLeft to center indicator
    const calculateScrollLeftToCenter = useCallback((): number | null => {
        const container = scrollContainerRef.current;
        const indicator = indicatorRef.current;
        if (!container || !indicator) return null;
        const c = container.getBoundingClientRect();
        const i = indicator.getBoundingClientRect();
        const offsetFromLeft = i.left - c.left;
        const target = Math.max(
            0,
            Math.round(container.scrollLeft + offsetFromLeft - container.clientWidth / 2 + i.width / 2)
        );
        return target;
    }, [indicatorRef, scrollContainerRef, autoScorll]);

    // smooth scroll using rAF
    const smoothScrollTo = useCallback(
        (targetLeft: number) => {
            const container = scrollContainerRef.current;
            if (!container) return;
            const start = container.scrollLeft;
            const distance = targetLeft - start;
            const startTime = performance.now();

            if (rafRef.current) cancelAnimationFrame(rafRef.current);

            const animate = (now: number) => {
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / smoothScrollDuration, 1);
                const eased =
                    progress < 0.5
                        ? 4 * progress * progress * progress
                        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

                container.scrollLeft = start + distance * eased;

                if (progress < 1) {
                    rafRef.current = requestAnimationFrame(animate);
                } else {
                    rafRef.current = null;
                }
            };

            rafRef.current = requestAnimationFrame(animate);
        },
        [scrollContainerRef, smoothScrollDuration, autoScorll]
    );

    // native scroll fallback
    const nativeScrollTo = useCallback(
        (targetLeft: number) => {
            const container = scrollContainerRef.current;
            if (!container) return;
            container.scrollTo({ left: targetLeft, behavior: scrollBehavior });
        },
        [scrollContainerRef, scrollBehavior, autoScorll]
    );

    // main API: scrollToIndicator (programmatic)
    const scrollToIndicator = useCallback(() => {
        const target = calculateScrollLeftToCenter();
        if (target === null) return;

        // mark programmatic so scroll handler ignores it
        programmaticScrollRef.current = true;
        if (programmaticTimerRef.current) {
            clearTimeout(programmaticTimerRef.current);
            programmaticTimerRef.current = null;
        }

        setIsUserScrolling(true);

        if (scrollBehavior === "smooth") smoothScrollTo(target);
        else nativeScrollTo(target);

        onScrollIntoView?.(target);

        // Clear programmatic flag after estimated animation time
        programmaticTimerRef.current = setTimeout(() => {
            programmaticScrollRef.current = false;
            setIsUserScrolling(false);
        }, (scrollBehavior === "smooth" ? smoothScrollDuration : 100) + 30);
    }, [
        calculateScrollLeftToCenter,
        nativeScrollTo,
        onScrollIntoView,
        scrollBehavior,
        smoothScrollDuration,
        smoothScrollTo,
        autoScorll
    ]);

    // IntersectionObserver: visibility changes
    useEffect(() => {
        const indicator = indicatorRef.current;
        const container = scrollContainerRef.current;
        if (!indicator || !container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                const visible = entry.isIntersecting;
                setIsVisible(visible);
                onVisibilityChange?.(visible);

                if (visible) {
                    // Indicator became visible: clear manual override so auto-scroll can resume
                    manualOverrideRef.current = false;
                } else {
                    // Indicator out of view: auto-scroll only if user hasn't manually overridden
                    if (!manualOverrideRef.current && !programmaticScrollRef.current) {
                        scrollToIndicator();
                    }
                }
            },
            { root: container, threshold: intersectionThreshold }
        );

        observer.observe(indicator);
        intersectionRef.current = observer;

        return () => {
            observer.disconnect();
            intersectionRef.current = null;
        };
    }, [indicatorRef, scrollContainerRef, intersectionThreshold, onVisibilityChange, scrollToIndicator, autoScorll]);

    // Scroll listener: immediate manual override on user scroll start, debounced check on end
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        lastScrollLeftRef.current = container.scrollLeft;

        const onScroll = () => {
            const current = container.scrollLeft;
            const delta = Math.abs(current - lastScrollLeftRef.current);

            if (delta <= 0) return;

            // If programmatic scroll is happening, ignore manual override
            if (programmaticScrollRef.current) {
                lastScrollLeftRef.current = current;
                // still debounce isUserScrolling clearing
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => {
                    setIsUserScrolling(false);
                    debounceRef.current = null;
                }, scrollDebounceMs);
                return;
            }

            // THIS IS USER SCROLL — immediately set manual override true (disable auto-scroll)
            manualOverrideRef.current = true;
            setIsUserScrolling(true);

            lastScrollLeftRef.current = current;

            // Debounced scroll end: when user stops scrolling, check visibility and decide override
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                setIsUserScrolling(false);
                debounceRef.current = null;

                // After user stopped, check indicator visibility
                const visibleNow = isIndicatorVisibleByThreshold();
                if (visibleNow) {
                    // If indicator is visible after user stopped -> clear manual override -> enable auto-scroll
                    manualOverrideRef.current = false;
                } else {
                    // If indicator still outside -> keep manual override true (auto-scroll remains disabled)
                    manualOverrideRef.current = true;
                }
            }, scrollDebounceMs);
        };

        container.addEventListener("scroll", onScroll, { passive: true });

        return () => {
            container.removeEventListener("scroll", onScroll);
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
        };
    }, [isIndicatorVisibleByThreshold, scrollContainerRef, scrollDebounceMs, autoScorll]);

    // Initial auto-scroll on mount (one time)
    useEffect(() => {
        if (!autoScrollOnMount) return;
        if (didAutoScrollOnMountRef.current) return;

        mountTimerRef.current = setTimeout(() => {
            didAutoScrollOnMountRef.current = true;
            scrollToIndicator();
        }, 3000);

        return () => {
            if (mountTimerRef.current) {
                clearTimeout(mountTimerRef.current);
                mountTimerRef.current = null;
            }
        };
    }, [autoScrollOnMount, scrollToIndicator, autoScorll]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (mountTimerRef.current) clearTimeout(mountTimerRef.current);
            if (intersectionRef.current) {
                intersectionRef.current.disconnect();
                intersectionRef.current = null;
            }
        };
    }, []);

    const getVisibilityState = useCallback(() => ({ isVisible, isUserScrolling }), [isVisible, isUserScrolling]);

    return {
        isVisible,
        isUserScrolling,
        scrollToIndicator,
        getVisibilityState,
    };
};

export type { UseRealtimeTrackerVisibilityProps, UseRealtimeTrackerVisibilityReturn };
